import type { Option } from '../types';

/**
 * The engine's first multi-select field (HW Index Phase 5). `exclusiveValue`
 * ports HW Index's `calculateCheckbox()` — checking "None of the above"
 * clears every other checked box and vice versa — as three lines instead of
 * the legacy's ~85-line jQuery function. See
 * docs/calculator-engine-architecture.md §5's "one place I am consciously
 * generalizing on a single data point" note: only HW Index uses this today.
 */
export function CheckboxGroup({
  id,
  value,
  options,
  exclusiveValue,
  onChange,
  onBlur,
  error,
}: {
  id: string;
  value: (string | number)[] | null;
  options: Option[];
  exclusiveValue?: string | number;
  onChange: (v: (string | number)[]) => void;
  onBlur: () => void;
  error: string | null;
}) {
  const selected = value ?? [];

  function toggle(optValue: string | number) {
    const isExclusive = optValue === exclusiveValue;
    if (isExclusive) {
      onChange(selected.includes(optValue) ? [] : [optValue]);
      return;
    }
    const withoutExclusive = selected.filter((v) => v !== exclusiveValue);
    onChange(
      withoutExclusive.includes(optValue)
        ? withoutExclusive.filter((v) => v !== optValue)
        : [...withoutExclusive, optValue],
    );
  }

  return (
    <div className="hsab-calc__checkbox-group" role="group" aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined}>
      {options.map((opt) => {
        const optId = `${id}-${opt.value}`;
        return (
          // Tooltip sits outside the <label> — see RadioGroup.tsx for why.
          <span key={String(opt.value)} className="hsab-calc__checkbox-option-row">
            <label htmlFor={optId} className="hsab-calc__checkbox-option">
              <input
                type="checkbox"
                id={optId}
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                onBlur={onBlur}
              />
              <span>{opt.label}</span>
              {opt.hint && <span className="hsab-calc__checkbox-hint">{opt.hint}</span>}
            </label>
            {opt.tooltip && (
              <span className="hsab-calc__tooltip" tabIndex={0} title={opt.tooltip} aria-label={opt.tooltip}>
                ?
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
